"use client";

import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Clock, Sparkles, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import LazyImage from "./utils/lazy-images";
import LogoWithName from "./utils/logo_with_name";
import { gradient } from "./utils/constants";

const customEase = [0.16, 1, 0.3, 1] as const;

// Smart navigation handler
export const handleNavClick = (href: string) => {
  if (href.startsWith("/#")) {
    if (window.location.pathname !== "/") {
      window.location.href = href;
    } else {
      const id = href.replace("/#", "");
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  } else if (href === "/") {
    window.location.pathname === "/"
      ? window.scrollTo({ top: 0, behavior: "smooth" })
      : (window.location.href = "/");
  } else {
    window.location.href = href;
  }
};

// ─── Animation variants ───────────────────────────────────────────────────────
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.65, delay, ease: customEase },
});

const fadeLeft = (delay = 0) => ({
  initial: { opacity: 0, x: -32 },
  animate: { opacity: 1, x: 0 },
  transition: { duration: 0.7, delay, ease: customEase },
});

const fadeRight = (delay = 0) => ({
  initial: { opacity: 0, x: 32 },
  animate: { opacity: 1, x: 0 },
  transition: { duration: 0.7, delay, ease: customEase },
});

// ─── Image grid data ─────────────────────────────────────────────────────────
const images = [
  {
    src: "/assets/retouching.png",
    alt: "Retouching",
    label: "Retouching",
    priority: true,
  },
  {
    src: "/assets/bg_removal.png",
    alt: "Background removal",
    label: "BG Removal",
    priority: false,
  },
  {
    src: "/assets/clipping_path.png",
    alt: "Clipping path",
    label: "Clipping Path",
    priority: false,
  },
  {
    src: "/assets/ghost_mannequin.png",
    alt: "Ghost mannequin",
    label: "Ghost Mannequin",
    priority: false,
  },
];

// ─── Stat pills ───────────────────────────────────────────────────────────────
const stats = [
  { icon: Clock, value: "24h-48h", label: "Turnaround" },
  { icon: Sparkles, value: "100%", label: "Hand-crafted" },
  { icon: RotateCcw, value: "Free", label: "First revision" },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function BackgroundBlobs() {
  return (
    <div
      className="pointer-events-none absolute inset-0 overflow-hidden"
      aria-hidden
    >
      {/* Top-left blob */}
      <div className="absolute -left-24 -top-24 h-105 w-105 animate-pulse rounded-full bg-blue-500/15 blur-[80px]" />
      {/* Bottom-right blob */}
      <div
        className="absolute -bottom-24 -right-16 h-125 w-125 animate-pulse rounded-full bg-purple-500/15 blur-[100px]"
        style={{ animationDelay: "1.5s" }}
      />
      {/* Centre accent */}
      <div className="absolute left-1/2 top-1/2 h-64 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full bg-indigo-400/10 blur-[60px]" />
    </div>
  );
}

function BadgePill() {
  return (
    <motion.div {...fadeUp(0)} className="mb-6 inline-flex">
      <span className="inline-flex items-center gap-2 rounded-full border border-blue-200/60 bg-blue-50/80 px-4 py-1.5 text-xs font-semibold tracking-wide text-blue-700 shadow-sm backdrop-blur-sm">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-blue-500" />
        Professional Photo Editing Services
      </span>
    </motion.div>
  );
}

function Headline() {
  return (
    <motion.h1
      {...fadeUp(0.1)}
      className="mb-5 text-4xl font-bold leading-tight tracking-tight text-gray-600 sm:text-5xl lg:text-6xl xl:text-7xl"
    >
      Transform your{" "}
      <span className={`${gradient} bg-clip-text text-transparent`}>
        Images
      </span>{" "}
      with{" "}
      <span className="mt-1 inline-block align-middle">
        <LogoWithName size={64} />
      </span>
    </motion.h1>
  );
}

function SubText() {
  return (
    <motion.p
      {...fadeUp(0.2)}
      className="mb-8 max-w-lg text-base leading-relaxed text-slate-600 sm:text-lg"
    >
      We&apos;re a fresh, dedicated team passionate about photo editing. Every
      image gets our full attention — because your first impression of us
      matters as much as yours does to your customers.
    </motion.p>
  );
}

function CTAButtons() {
  return (
    <motion.div
      {...fadeUp(0.3)}
      className="flex flex-col gap-3 sm:flex-row sm:gap-4"
    >
      <Button
        size="lg"
        onClick={() => handleNavClick("/#contact")}
        className={`group relative overflow-hidden ${gradient} px-7 py-5 text-base font-semibold text-white shadow-lg shadow-blue-500/25 transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/30 hover:-translate-y-0.5`}
      >
        Get a Quote
        <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
      </Button>

      {/* <Button
        size="lg"
        variant="outline"
        onClick={() => handleNavClick("/#portfolio")}
        className="border-slate-200 bg-white/70 px-7 py-5 text-base font-semibold text-slate-700 backdrop-blur-sm transition-all duration-300 hover:border-blue-300 hover:bg-white hover:text-blue-600 hover:-translate-y-0.5"
      >
        View Our Work
      </Button> */}
    </motion.div>
  );
}

function StatPills() {
  return (
    <motion.div {...fadeUp(0.4)} className="flex flex-wrap gap-3">
      {stats.map(({ icon: Icon, value, label }) => (
        <div
          key={label}
          className="flex items-center gap-2.5 rounded-xl border border-slate-100 bg-white/70 px-4 py-2.5 shadow-sm backdrop-blur-sm"
        >
          <div
            className={`flex h-8 w-8 items-center justify-center rounded-lg ${gradient} shadow-sm`}
          >
            <Icon className="h-4 w-4 text-white" />
          </div>
          <div>
            <div className="text-base font-bold leading-none text-slate-800">
              {value}
            </div>
            <div className="mt-0.5 text-[11px] font-medium leading-none text-slate-500">
              {label}
            </div>
          </div>
        </div>
      ))}
    </motion.div>
  );
}

function ImageGrid() {
  return (
    <motion.div {...fadeRight(0.25)} className="relative">
      {/* Outer glow card */}
      <div className="w-max relative rounded-3xl border border-white/40 bg-white/10 p-5 shadow-2xl shadow-blue-500/10 backdrop-blur-md sm:p-7">
        {/* Gradient glow behind card */}
        <div
          className={`absolute inset-0 -z-10 rounded-3xl ${gradient} opacity-10 blur-2xl`}
        />

        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          {/* Column 1 — aligned to top */}
          <div className="flex flex-col gap-3 sm:gap-4 w-62.5 shrink-0">
            {images.slice(0, 2).map(({ src, alt, label, priority }) => (
              <ImageCard
                key={src}
                src={src}
                alt={alt}
                label={label}
                priority={priority}
              />
            ))}
          </div>
          {/* Column 2 — offset down */}
          <div className="flex flex-col gap-3 pt-6 sm:gap-4 sm:pt-8 w-62.5 shrink-0">
            {images.slice(2, 4).map(({ src, alt, label, priority }) => (
              <ImageCard
                key={src}
                src={src}
                alt={alt}
                label={label}
                priority={priority}
                accent
              />
            ))}
          </div>
        </div>

        {/* Floating quality badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.5, ease: customEase }} // ← Updated
          className="absolute -bottom-4 -left-4 flex items-center gap-2 rounded-2xl border border-white/60 bg-white px-4 py-2.5 shadow-xl"
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-linear-to-br from-green-400 to-emerald-500 text-white">
            <Sparkles className="h-4 w-4" />
          </div>
          <div>
            <p className="text-xs font-bold text-slate-800">Premium Quality</p>
            <p className="text-[10px] text-slate-500">Every pixel perfected</p>
          </div>
        </motion.div>

        {/* Floating turnaround badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: -10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.5, ease: customEase }} // ← Updated
          className="absolute -right-4 top-0 flex items-center gap-2 rounded-2xl border border-white/60 bg-white px-3 py-2 shadow-xl"
        >
          <Clock className="h-4 w-4 text-blue-600" />
          <span className="text-xs font-bold text-slate-800">
            24h-48h Delivery
          </span>
        </motion.div>
      </div>
    </motion.div>
  );
}

function ImageCard({
  src,
  alt,
  label,
  priority = false,
  accent = false,
}: {
  src: string;
  alt: string;
  label: string;
  priority?: boolean;
  accent?: boolean;
}) {
  return (
    <div
      className={`group overflow-hidden rounded-2xl border p-2 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md ${
        accent
          ? `border-white/30 ${gradient} bg-opacity-20`
          : "border-slate-100 bg-white/60 backdrop-blur-sm"
      }`}
    >
      <LazyImage
        src={src}
        alt={alt}
        width={200}
        height={200}
        priority={priority}
        className="h-full w-full rounded-lg object-cover"
      />
    </div>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────────────
export function Hero() {
  const shouldReduce = useReducedMotion();

  return (
    <section
      id="home"
      className="relative w-full overflow-hidden bg-linear-to-b from-slate-50/80 to-white"
    >
      <BackgroundBlobs />

      {/* Subtle grid texture */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            "linear-gradient(#64748b 1px, transparent 1px), linear-gradient(90deg, #64748b 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
        aria-hidden
      />

      <div className="container relative z-10 mx-auto px-4 pb-20 pt-20 sm:px-6 lg:px-8 lg:pb-28">
        <div className="grid grid-cols-1 items-center lg:grid-cols-2">
          {/* ── Left: Copy ────────────────────────────────────────────────── */}
          <motion.div
            {...(shouldReduce ? {} : fadeLeft(0))}
            className="flex flex-col"
          >
            <BadgePill />
            <Headline />
            <SubText />
            <div className="flex justify-between items-center">
              <StatPills />
              <CTAButtons />
            </div>
          </motion.div>

          {/* ── Right: Image grid ─────────────────────────────────────────── */}
          <div className="relative flex justify-center">
            <div className="w-full max-w-110">
              <ImageGrid />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
